import { EventObject } from '@xstate/store-react';
import { store } from '../../store';
import { log } from '@domgen/shared/src/utils/log';
import { posthog } from 'posthog-js';
import { AppStatus } from '../../../enums/appStatus';
import { StoreEvent } from '../../../enums/storeEvent';
import { StoreContext } from '../../../models/store/context';
import { saveSession } from '../../../services/session/save';

/**
 * @description Session Set
 * Upon the session being created server side we set it in the store and need to use that to:
 * 1. Get the catalogue if we don't have it already (as this is required for the app to function)
 * 2. Start the session timer which tracks how long the user has been on the app for (used for analytics and session timeout)
 * 3. Save the session to session storage so it can be retrieved on app load and used for subsequent API calls
 * 4. Register the session ID with PostHog so it can be used in analytics events
 * @param {StoreContext} context - The current store context
 * @param {EventObject & { data?: { id: string } }} event - The event object containing the session ID in event.data.id
 * @param {{ effect: (effect: () => Promise<void>) => void }} enqueue - An object with an effect function to enqueue side effects
 * @returns {StoreContext} Updated context with session set and catalogue loading if not already present
 */
export const setSession = (
  context: StoreContext,
  event: EventObject & { data?: { id: string } },
  enqueue: { effect: (effect: () => Promise<void>) => void },
): StoreContext => {
  if (event.data === undefined || event.data.id === undefined) {
    log.error({
      message: StoreEvent.SET_SESSION,
      data: { error: 'Session ID is missing in event data' },
    });
    return context;
  } else {
    log.debug({ message: StoreEvent.SET_SESSION, data: event.data });
    const session = {
      id: event.data.id,
      dateCreated: new Date().toISOString(),
    };

    enqueue.effect(async () => {
      if (!context.app.catalogue || (context && context.app.catalogue.length === 0)) {
        store.send({ type: StoreEvent.GET_CATALOGUE });
      }
      store.send({ type: StoreEvent.START_SESSION_TIMER });
    });

    const updatedContext = {
      ...context,
      app: {
        ...context.app,
        session,
        status: context.app.catalogue ? AppStatus.READY : AppStatus.LOADING,
      },
    };

    if (updatedContext.app.catalogue) {
      saveSession(updatedContext);
    }

    posthog.register({ appSessionId: session.id });
    return updatedContext;
  }
};
